import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ShoppingCart, LogOut, Users } from 'lucide-react';
import UserManagement from '@/components/UserManagement';
import ReminderButton from '@/components/ReminderButton';
import ReminderPrompt from '@/components/ReminderPrompt';

export default function Navbar({ stats }) {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const [usersOpen, setUsersOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  const navLinks = [
    { to: '/', label: 'Dashboard', testid: 'nav-dashboard' },
    { to: '/supplier-view', label: 'Supplier View', testid: 'nav-supplier-view' },
    { to: '/pacer', label: 'Pacer', testid: 'nav-pacer' }
  ];

  return (
    <>
      <header className="bg-white/90 backdrop-blur-xl border-b border-zinc-200 sticky top-0 z-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 bg-emerald-600 flex items-center justify-center">
                <ShoppingCart className="h-4 w-4 text-white" />
              </div>
              <span
                data-testid="navbar-title"
                className="text-base font-bold tracking-tight hidden sm:block"
                style={{ fontFamily: "'Chivo', sans-serif" }}
              >
                OrderGenie
              </span>
            </div>

            <nav className="flex items-center gap-1">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={l.testid}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors border-b-2 ${
                    pathname === l.to
                      ? 'text-zinc-900 border-emerald-600'
                      : 'text-zinc-500 border-transparent hover:text-zinc-900 hover:border-zinc-300'
                  }`}
                >
                  {l.label}
                </Link>
              ))}
            </nav>

            {stats && (
              <div className="hidden md:flex items-center gap-4 text-xs text-zinc-500">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-semibold text-zinc-900">{stats.total_products || 0}</span>
                  <span>Products</span>
                </div>
                <div className="w-px h-4 bg-zinc-200" />
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-semibold text-zinc-900">{stats.total_uploads || 0}</span>
                  <span>Uploads</span>
                </div>
                <div className="w-px h-4 bg-zinc-200" />
                <div className="flex items-center gap-1.5">
                  <span className="font-mono font-semibold text-zinc-900">{stats.total_suppliers || 0}</span>
                  <span>Suppliers</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && (
              <Button
                data-testid="manage-users-btn"
                variant="ghost"
                size="sm"
                onClick={() => setUsersOpen(true)}
                className="rounded-none text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 h-8 px-3 gap-1.5"
              >
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline text-xs">Users</span>
              </Button>
            )}
            <span className="text-xs text-zinc-500 hidden sm:block">{user?.email}</span>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="sm"
              onClick={logout}
              className="rounded-none text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 h-8 px-3"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {isAdmin && <UserManagement open={usersOpen} onOpenChange={setUsersOpen} />}
      <ReminderButton />
      <ReminderPrompt />
    </>
  );
}
